import React, { useEffect, useRef, useState } from 'react';
import { HandSwipeLeft, HandSwipeRight } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

/**
 * Wraps any horizontally-scrollable content and gives users a
 * non-obstructive hint that the content extends past the viewport.
 *
 * Design:
 *  – No edge overlay (previous version painted a bright fade that
 *    hid the last column, especially in dark mode).
 *  – A slim "swipe" pill sits above the top-right corner of the
 *    scroll container only when overflow exists AND we haven't
 *    scrolled to that edge yet. It auto-hides once the user
 *    reaches the end.
 *  – Custom themed scrollbar so the bar itself reads as an
 *    intentional part of the design.
 *
 * Usage:
 *   <ScrollShadow>
 *     <Table>...</Table>
 *   </ScrollShadow>
 */
export default function ScrollShadow({ children, className = '', minChildWidth = 640 }) {
  const scrollRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const { direction } = useLanguage();
  const { t } = useTranslation();
  const isRtl = direction === 'rtl';

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const absLeft = Math.abs(scrollLeft);
      const overflow = scrollWidth - clientWidth > 4;
      setHasOverflow(overflow);
      setAtEnd(!overflow || absLeft + clientWidth >= scrollWidth - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [children]);

  const HintIcon = isRtl ? HandSwipeLeft : HandSwipeRight;
  const hintVisible = hasOverflow && !atEnd;

  return (
    <div className={`relative ${className}`}>
      {/* Swipe hint pill — sits ABOVE content, animates a subtle
           bounce, and disappears once the user has scrolled to the
           opposite edge. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute -top-2 z-10 transition-opacity duration-300 ${
          hintVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ [isRtl ? 'left' : 'right']: 8 }}
      >
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold
                     bg-[#EDE9FE] text-[#5B21B6]
                     dark:bg-violet-500/20 dark:text-violet-200
                     border border-[#C4BFF0]/60 dark:border-violet-400/30
                     shadow-sm animate-[nudge_1.8s_ease-in-out_infinite]"
        >
          <HintIcon size={13} weight="fill" className="shrink-0" />
          {t('swipe_hint') || 'اسحب'}
        </span>
      </div>

      <div
        ref={scrollRef}
        className="overflow-x-auto scroll-shadow-scroller"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div style={{ minWidth: minChildWidth }}>{children}</div>
      </div>
    </div>
  );
}
